import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { SkeletonList } from '@/components/ui/skeleton-card'
import { PostCard } from '@/components/home/post-card'
import { PageTransition, EmptyState } from '@/components/ui/ux-enhancer'
import { User, Calendar, FileText, MessageSquare } from 'lucide-react'
import { apiAdapter } from '@/lib/api-adapter'

interface UserProfile {
  id: string
  username: string
  nickname: string
  avatar: string
  bio: string
  postsCount: number
  commentsCount: number
  createdAt: string
}

interface UserPost {
  id: string
  userId: string
  content: string
  author: { name: string; avatar?: string; school: string; level: number }
  images: string[]
  category: string
  createdAt: Date
  time: string
  likes: number
  comments: number
  tags: string[]
}

function formatTime(dateString: string): string {
  const now = new Date()
  const postTime = new Date(dateString)
  const diffInMinutes = Math.floor((now.getTime() - postTime.getTime()) / (1000 * 60))
  if (diffInMinutes < 1) return '刚刚'
  if (diffInMinutes < 60) return `${diffInMinutes}分钟前`
  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `${diffInHours}小时前`
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `${diffInDays}天前`
  return postTime.toLocaleDateString('zh-CN')
}

export default function UserProfilePage() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [posts, setPosts] = useState<UserPost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!userId) return

    const loadData = async () => {
      setIsLoading(true)
      setError('')
      try {
        const [userRes, postsRes] = await Promise.all([
          apiAdapter.users.getUser(userId),
          apiAdapter.posts.getUserPosts(userId, { page: 1, limit: 20 })
        ])

        if (userRes.success && userRes.data?.user) {
          const u = userRes.data.user
          setProfile({
            id: u._id,
            username: u.username,
            nickname: u.nickname || u.username,
            avatar: u.avatar || '',
            bio: u.bio || '这个人很懒，什么都没有写~',
            postsCount: u.postsCount || 0,
            commentsCount: u.commentsCount || 0,
            createdAt: u.createdAt
          })
        } else {
          setError(userRes.message || '用户不存在')
        }

        if (postsRes.success && postsRes.data?.posts) {
          setPosts(postsRes.data.posts.map((p: any) => ({
            id: p._id,
            userId: p.author?._id || '',
            content: p.content || '',
            author: {
              name: p.isAnonymous ? '匿名用户' : (p.author?.nickname || p.author?.username || '未知用户'),
              avatar: p.isAnonymous ? '' : (p.author?.avatar || ''),
              school: '',
              level: 1
            },
            images: p.images || [],
            category: p.category || '其他',
            createdAt: new Date(p.createdAt),
            time: formatTime(p.createdAt),
            likes: p.likeCount || 0,
            comments: p.commentCount || 0,
            tags: p.tags || []
          })))
        }
      } catch (err: any) {
        setError(err?.message || '加载失败，请稍后重试')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [userId])

  if (isLoading) {
    return (
      <PageTransition>
        <div className="space-y-4">
          <Card>
            <CardContent className="p-6 flex items-center space-x-4">
              <div className="w-20 h-20 bg-gray-200 rounded-full animate-pulse" />
              <div className="space-y-2 flex-1">
                <div className="w-40 h-5 bg-gray-200 rounded animate-pulse" />
                <div className="w-64 h-4 bg-gray-200 rounded animate-pulse" />
              </div>
            </CardContent>
          </Card>
          <SkeletonList count={3} />
        </div>
      </PageTransition>
    )
  }

  if (error || !profile) {
    return (
      <PageTransition>
        <div className="max-w-lg mx-auto py-16">
          <EmptyState
            icon={<User className="w-16 h-16 text-gray-300" />}
            title="用户不存在"
            description={error || '该用户不存在或已被禁用'}
            action={
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                回到首页
              </button>
            }
          />
        </div>
      </PageTransition>
    )
  }

  return (
    <PageTransition>
      <div className="space-y-4 lg:space-y-6">
        {/* 用户资料卡 */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <Avatar className="w-20 h-20 ring-2 ring-gray-100">
                <AvatarImage src={profile.avatar} />
                <AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-400 text-white text-2xl font-medium">
                  {profile.nickname?.[0] || '?'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 space-y-2">
                <div className="flex items-center space-x-2">
                  <h1 className="text-xl font-bold text-gray-900">{profile.nickname}</h1>
                  <Badge variant="secondary">@{profile.username}</Badge>
                </div>
                <p className="text-gray-600 text-sm">{profile.bio}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                  <span className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>加入于 {new Date(profile.createdAt).toLocaleDateString('zh-CN')}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* 统计 */}
            <div className="grid grid-cols-2 gap-4 mt-6">
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600 flex items-center justify-center gap-2">
                  <FileText className="w-5 h-5" />
                  {profile.postsCount}
                </div>
                <div className="text-sm text-gray-600 mt-1">发布内容</div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600 flex items-center justify-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  {profile.commentsCount}
                </div>
                <div className="text-sm text-gray-600 mt-1">评论数量</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 用户发布的帖子 */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">TA 的发布</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {posts.length > 0 ? (
              posts.map(post => (
                <PostCard key={post.id} post={post} />
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                该用户还没有发布任何内容
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageTransition>
  )
}
